import { AUTH_TOKEN, JWT_TOKEN } from '../constants/session-storage-keys';

const ServiceRoot = {
  MAIN: 'MAIN',
  MICROSERVICE: 'MICROSERVICE',
  PYTHON: 'PYTHON',
  DEAL_COLLAB: 'DEAL_COLLAB',
  MICROSERVICE_WITH_JWT: 'MICROSERVICE_WITH_JWT',
  PROCUREMENT: 'PROCUREMENT',
  UAM: 'UAM',
};

export const URLs = {
  PREPROD_GATEWAY: 'https://preprod-gateway.leegality.com',
  SANDBOX_GATEWAY: 'https://sandbox-gateway.leegality.com',
  DEV_GATEWAY: 'https://dev-gateway.leegality.com',
  PROD_GATEWAY: 'https://api-gateway.leegality.com',
  PREPROD: 'https://preprod.leegality.com',
  SANDBOX: 'https://sandbox.leegality.com',
  PROD: 'https://app1.leegality.com',
  DC_DEV: 'https://dc-dev.leegality.com',
  DC_PREPROD: 'https://dc-preprod.leegality.com',
  DC_SANDBOX: 'https://dc-sandbox.leegality.com',
  DC_PROD: 'https://dc.leegality.com',
  UAM_PREPROD: 'https://uam-preprod.leegality.com',
  UAM_SANDBOX: 'https://uam-sandbox.leegality.com',
  UAM_PROD: 'https://uam.leegality.com',
  PREPROD_PROCUREMENT: 'https://preprod-procurement.leegality.com',
  SANDBOX_PROCUREMENT: 'https://sandbox-procurement.leegality.com',
  PROD_PROCUREMENT: 'https://procurement.leegality.com',
};

export const EnvTypes = {
  PREPROD: 'preprod',
  SANDBOX: 'sandbox',
  DEV: 'dev',
  PROD: 'prod',
};

const ServicePathTypes = {
  AUTH: 'auth',
  CLOUD_CONFIG: 'cloudconfig',
  PDF_TEMPLATE: 'pdf-template',
  FILE_UPLOAD: 'file-upload',
  PAYMENT_COLLECT: 'payment-collect',
  PHYSICAL_SIGNATURE: 'physical-signature',
  WALLET: 'wallet',
  STAMPING: 'stamping',
  DEAL_COLLAB: 'deal-collab',
  WORKLFOW_MANAGER: 'workflow-manager',
  WORKFLOW_RUNNER: 'workflow-runner',
  DIS: 'document-infrastructure',
  TEMPLATE_SERVICE: 'template',
  PROCUREMENT: 'procurement',
  UAM_PANEL: 'uam-panel',
};

const getAuthToken = () => JSON.parse(sessionStorage.getItem(AUTH_TOKEN));
const getJWTToken = () => JSON.parse(sessionStorage.getItem(JWT_TOKEN));

const getServiceConfig = () => ([
  {
    serviceType: ServiceRoot.MICROSERVICE,
    headers: getAuthToken() ? { 'x-auth-token': getAuthToken() } : {},
    servicePaths: [
      ServicePathTypes.AUTH,
      ServicePathTypes.CLOUD_CONFIG,
      ServicePathTypes.FILE_UPLOAD,
      ServicePathTypes.PAYMENT_COLLECT,
      ServicePathTypes.PHYSICAL_SIGNATURE,
      ServicePathTypes.WALLET,
      ServicePathTypes.STAMPING,
    ],
    envUrls: {
      [EnvTypes.PREPROD]: URLs.PREPROD_GATEWAY,
      [EnvTypes.SANDBOX]: URLs.SANDBOX_GATEWAY,
      [EnvTypes.PROD]: URLs.PROD_GATEWAY,
      [EnvTypes.DEV]: URLs.PREPROD_GATEWAY,
    },
  },
  {
    serviceType: ServiceRoot.MICROSERVICE_WITH_JWT,
    headers: getJWTToken() ? { Authorization: getJWTToken() } : {},
    servicePaths: [
      ServicePathTypes.WORKLFOW_MANAGER,
      ServicePathTypes.WORKFLOW_RUNNER,
      ServicePathTypes.DIS,
      ServicePathTypes.TEMPLATE_SERVICE,
    ],
    envUrls: {
      [EnvTypes.PREPROD]: URLs.PREPROD_GATEWAY,
      [EnvTypes.SANDBOX]: URLs.SANDBOX_GATEWAY,
      [EnvTypes.PROD]: URLs.PROD_GATEWAY,
      [EnvTypes.DEV]: URLs.PREPROD_GATEWAY,
    },
  },
  {
    serviceType: ServiceRoot.PYTHON,
    servicePaths: [ServicePathTypes.PDF_TEMPLATE],
    headers: getAuthToken() ? { 'x-auth-token': getAuthToken() } : {},
    envUrls: {
      [EnvTypes.PREPROD]: URLs.PREPROD_GATEWAY,
      [EnvTypes.SANDBOX]: URLs.SANDBOX_GATEWAY,
      [EnvTypes.PROD]: URLs.PROD_GATEWAY,
      [EnvTypes.DEV]: URLs.PREPROD_GATEWAY,
    },
  },
  {
    serviceType: ServiceRoot.DEAL_COLLAB,
    servicePaths: [ServicePathTypes.DEAL_COLLAB],
    headers: getAuthToken() ? { 'x-auth-token': getAuthToken() } : {},
    envUrls: {
      [EnvTypes.PREPROD]: URLs.DC_PREPROD,
      [EnvTypes.SANDBOX]: URLs.DC_SANDBOX,
      [EnvTypes.PROD]: URLs.DC_PROD,
    },
  },
  {
    serviceType: ServiceRoot.UAM,
    servicePaths: [ServicePathTypes.UAM_PANEL],
    headers: getAuthToken() ? { 'x-auth-token': getAuthToken() } : {},
    envUrls: {
      [EnvTypes.PREPROD]: URLs.UAM_PREPROD,
      [EnvTypes.SANDBOX]: URLs.UAM_SANDBOX,
      [EnvTypes.PROD]: URLs.UAM_PROD,
    },
  },
  {
    serviceType: ServiceRoot.PROCUREMENT,
    servicePaths: [ServicePathTypes.PROCUREMENT],
    envUrls: {
      [EnvTypes.PREPROD]: URLs.PREPROD_PROCUREMENT,
      [EnvTypes.SANDBOX]: URLs.SANDBOX_PROCUREMENT,
      [EnvTypes.PROD]: URLs.PROD_PROCUREMENT,
      [EnvTypes.DEV]: URLs.PREPROD_PROCUREMENT,
    },
  },
]);

const getDefaultService = () => ({
  serviceType: ServiceRoot.MAIN,
  servicePaths: [],
  headers: getAuthToken() ? { 'x-auth-token': getAuthToken() } : {},
  envUrls: {
    [EnvTypes.DEV]: URLs.PREPROD,
    [EnvTypes.PREPROD]: URLs.PREPROD,
    [EnvTypes.SANDBOX]: URLs.SANDBOX,
    [EnvTypes.PROD]: URLs.PROD,
  },
});

const getServiceByUrl = (url) => {
  const modules = url.split('/');
  return getServiceConfig().find(({ servicePaths }) => (
    servicePaths.includes(modules[1]))) || getDefaultService();
};

export const getServiceAndBaseUrl = (url = '') => {
  const env = process.env.REACT_APP_STAGE || EnvTypes.PREPROD;

  const service = getServiceByUrl(url);

  return {
    service,
    baseURL: service.envUrls[env],
  };
};

export const resolveGatewayUrl = () => {
  const env = process.env.REACT_APP_STAGE || EnvTypes.PREPROD;
  switch (env) {
    case EnvTypes.PROD: return URLs.PROD_GATEWAY;
    case EnvTypes.SANDBOX: return URLs.SANDBOX_GATEWAY;
    case EnvTypes.DEV:
      return URLs.DEV_GATEWAY;
    case EnvTypes.PREPROD:
    default:
      return URLs.PREPROD_GATEWAY;
  }
};
